import React, { useState, useEffect, useMemo } from 'react';
import { Spin, Tag } from 'antd';
import FullScreenModal from '../Components/Modals/FullScreenModal';
import TimelineRenewalsModal from './TimelineRenewalsModal';
import dayjs from "dayjs";
import isBetween from 'dayjs/plugin/isBetween';
import {
    primaryShades,
    secondaryShades,
    variantTokens,
    greyScaleTokens,
    statusTokens
} from "../../theme/theme";
import CardInformativeSection from './CardInformativeSection';
import ChartsConsumption from './ChartsConsumption';
import { OutlineButton } from '../Components/Buttons/Buttons';
import { getCatalogueData, getCounterData, getMainDomain, checkHistoryData } from '../../services/api/subscriptionApi';

dayjs.extend(isBetween);

// Costanti dei colori dal tema
const PRIMARY = variantTokens.primary;
const PRIMARY_300 = primaryShades.primary300;
const PRIMARY_200 = primaryShades.primary200;

const SECONDARY = variantTokens.secondary;
const SECONDARY_200 = secondaryShades.secondary200;
const SECONDARY_100 = secondaryShades.secondary100;

// Palette per i gradienti - utilizziamo le costanti del tema
const primaryLighter = PRIMARY_300;     // Versione più chiara del primary
const primaryLightest = PRIMARY_200;    // Versione ancora più chiara
const primaryVeryLight = greyScaleTokens.grey100; // Usato per sfumature quasi bianche
const secondaryLighter = SECONDARY_200; // Versione più chiara del secondary
const secondaryLightest = SECONDARY_100; // Versione ancora più chiara

/**
 * ConsumptionDetailsModal - Modal per visualizzare i dettagli del consumo di una subscription
 * 
 * @param {boolean} visible - Controlla se la modale è visibile
 * @param {function} onClose - Funzione chiamata quando la modale viene chiusa
 * @param {object} subscription - Dati della subscription selezionata
 */
const ConsumptionDetailsModal = ({
    visible = false,
    onClose,
    subscription = null
}) => {
    const [chartData, setChartData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [timelineModalVisible, setTimelineModalVisible] = useState(false);
    const [mainDomain, setMainDomain] = useState(null);
    const [hasHistoryData, setHasHistoryData] = useState(false);
    const [checkingHistory, setCheckingHistory] = useState(false);

    // Definizione dei gradienti per i grafici
    const defs = useMemo(() => [
        {
            id: 'monthlyGradient',
            type: 'linearGradient',
            colors: [
                { offset: 0, color: PRIMARY },
                { offset: 40, color: `${primaryLighter}DD` },
                { offset: 80, color: `${primaryLightest}CC` },
                { offset: 100, color: `${primaryVeryLight}BB` },
            ],
        },
        {
            id: 'consumedGradient',
            type: 'linearGradient',
            colors: [
                { offset: 0, color: PRIMARY },
                { offset: 40, color: `${primaryLighter}DD` },
                { offset: 80, color: `${primaryLightest}CC` },
                { offset: 100, color: `${primaryVeryLight}BB` },
            ],
        },
        {
            id: 'remainingGradient',
            type: 'linearGradient',
            colors: [
                { offset: 0, color: SECONDARY },
                { offset: 40, color: `${secondaryLighter}DD` },
                { offset: 80, color: `${secondaryLightest}CC` },
                { offset: 100, color: `${secondaryLightest}BB` },
            ],
        },
    ], []);

    // Mappatura dei colori per le barre
    const barChartFill = useMemo(() => [
        { match: '*', id: 'monthlyGradient' }
    ], []);

    // Mappatura dei colori per gli spicchi
    const pieChartFill = useMemo(() => [
        { match: { id: 'Consumed' }, id: 'consumedGradient' },
        { match: { id: 'Remaining' }, id: 'remainingGradient' },
    ], []);

    // Stile del tooltip personalizzato
    const tooltipStyle = useMemo(() => ({
        backgroundColor: 'white',
        padding: '0.5rem 0.75rem',
        border: '0.0625rem solid #e8e8e8',
        borderRadius: '0.125rem',
        boxShadow: '0 0.125rem 0.5rem rgba(0,0,0,0.15)',
    }), []);

    // Handle timeline modal
    const handleOpenTimelineModal = () => {
        setTimelineModalVisible(true);
    };

    const handleCloseTimelineModal = () => {
        setTimelineModalVisible(false);
    };

    // Calculate days left and progress based on startDate to endDate
    const calculateDaysLeft = () => {
        if (!subscription?.endDate || !subscription?.startDate) return { daysLeft: 'ND', percentage: 0, color: '#d9d9d9' };

        const today = dayjs();
        let startDate, endDate;

        // Handle different date formats for startDate
        if (typeof subscription.startDate === 'string') {
            startDate = dayjs(subscription.startDate);
        } else if (subscription.startDate.seconds) {
            startDate = dayjs.unix(subscription.startDate.seconds);
        } else {
            startDate = dayjs(subscription.startDate);
        }

        // Handle different date formats for endDate
        if (typeof subscription.endDate === 'string') {
            endDate = dayjs(subscription.endDate);
        } else if (subscription.endDate.seconds) {
            endDate = dayjs.unix(subscription.endDate.seconds);
        } else {
            endDate = dayjs(subscription.endDate);
        }

        const totalDays = endDate.diff(startDate, 'day');
        const daysLeft = endDate.diff(today, 'day');
        const daysPassed = today.diff(startDate, 'day');

        // Calculate percentage based on days passed vs total days
        const percentage = totalDays > 0 ? Math.min((daysPassed / totalDays) * 100, 100) : 0;

        // Determine color based on days left percentage
        const daysLeftPercentage = totalDays > 0 ? (daysLeft / totalDays) * 100 : 0;
        let color;

        if (daysLeftPercentage > 50) {
            color = '#52c41a'; // Green (Primary)
        } else if (daysLeftPercentage > 25) {
            color = '#faad14'; // Orange (Warning)
        } else if (daysLeft > 0) {
            color = '#ff4d4f'; // Red (Danger)
        } else {
            color = '#d9d9d9'; // Grey (Expired)
        }

        return {
            daysLeft: daysLeft > 0 ? daysLeft : 0,
            percentage: Math.max(percentage, 0),
            color
        };
    };

    const { daysLeft, percentage, color } = calculateDaysLeft();

    // Function to filter data by date range
    const filterDataByDateRange = (dataArray, startDate, endDate) => {
        const start = dayjs(startDate);
        const end = dayjs(endDate);

        return dataArray.filter(item => {
            // Creare correttamente una data dayjs con anno e mese specifici
            // Usare la stringa ISO per creare la data o il costruttore con parametri
            const itemDate = dayjs(`${item.year}-${String(item.month).padStart(2, '0')}-01`);
            return itemDate.isBetween(start, end, 'month', '[]');
        });
    };

    // Function to convert month number to month name
    const getMonthName = (monthKey) => {
        const [month] = monthKey.split('-');
        const monthNames = [
            'January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'
        ];
        return monthNames[parseInt(month) - 1] || monthKey;
    };

    // Main function to fetch and process data - SEMPLIFICATO
    const fetchConsumptionData = async () => {
        if (!subscription || !subscription.list_catalogues?.list_catalogues_refs || subscription.list_catalogues.list_catalogues_refs.length === 0) {
            return;
        }

        setLoading(true);
        try {
            // Step 1: Prendi il PRIMO catalogo dalla lista
            const firstCatalogueId = subscription.list_catalogues.list_catalogues_refs[0];

            // Step 2: Prendi i dati del catalogo
            const catalogueData = await getCatalogueData(firstCatalogueId);
            if (!catalogueData || !catalogueData.dataConsumption) {
                setChartData(null);
                return;
            }

            // Step 3: Prendi il contatore
            const counterData = await getCounterData(catalogueData.dataConsumption);
            if (!counterData) {
                setChartData(null);
                return;
            }

            // Step 4: Filtra per periodo e somma tutti i bandwidth_month
            const startDate = subscription.startDate;
            const endDate = subscription.endDate;

            let totalBandwidthMB = 0;
            const monthlyData = {};

            // Processa tutti gli array di bandwidth
            ['bandwidth_3dviewer', 'bandwidth_array', 'bandwidth_webVto'].forEach(serviceKey => {
                if (counterData[serviceKey] && Array.isArray(counterData[serviceKey])) {
                    const filteredData = filterDataByDateRange(counterData[serviceKey], startDate, endDate);

                    filteredData.forEach(item => {
                        const bandwidthMB = item.bandwidth_month || 0;
                        totalBandwidthMB += bandwidthMB;

                        // Per il grafico mensile
                        const monthKey = `${item.year}-${String(item.month).padStart(2, '0')}`;
                        if (!monthlyData[monthKey]) {
                            monthlyData[monthKey] = 0;
                        }
                        monthlyData[monthKey] += bandwidthMB;
                    });
                }
            });

            // Converti da MB a GB
            const totalConsumedGB = totalBandwidthMB / 1024;

            // Prepara i dati del grafico mensile (in GB)
            const monthlyChartData = Object.entries(monthlyData)
                .map(([month, bandwidthMB]) => ({
                    month: getMonthName(month), // Converti in nome del mese
                    monthKey: month, // Mantieni la chiave originale per l'ordinamento
                    bandwidth: parseFloat((bandwidthMB / 1024).toFixed(2)) // Converti in GB
                }))
                .sort((a, b) => a.monthKey.localeCompare(b.monthKey));

            // Il consumptionLimit è già in GB
            const consumptionLimit = subscription.list_catalogues?.consumptionLimit || 0;
            const remaining = Math.max(0, consumptionLimit - totalConsumedGB);

            const limitChartData = [
                {
                    id: 'Consumed',
                    value: parseFloat(totalConsumedGB.toFixed(2)),
                    color: primaryShades.primary300
                },
                {
                    id: 'Remaining',
                    value: parseFloat(remaining.toFixed(2)),
                    color: '#e9ecef'
                }
            ];

            setChartData({
                monthlyChart: monthlyChartData,
                limitChart: limitChartData,
                totalConsumed: parseFloat(totalConsumedGB.toFixed(2)),
                consumptionLimit,
                remaining: parseFloat(remaining.toFixed(2))
            });

        } catch (error) {
            setChartData(null);
        } finally {
            setLoading(false);
        }
    };

    // Fetch data when subscription changes
    useEffect(() => {
        if (visible && subscription) {
            fetchConsumptionData();
            getMainDomain(subscription, setMainDomain);
            checkHistoryData(subscription, setHasHistoryData, setCheckingHistory);
        }
    }, [visible, subscription]);

    if (!subscription) return null;

    return (
        <>
            <FullScreenModal
                visible={visible}
                onClose={onClose}
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <span>{`Consumption Details - ${subscription.name || 'N/A'}`}</span>
                        <Tag
                            color="success"
                            style={{
                                fontSize: '1rem',
                                fontWeight: 'bold',
                                padding: '0.25rem 0.5rem',
                                margin: 0,
                                backgroundColor: statusTokens.success,
                                borderColor: statusTokens.success,
                                color: variantTokens.white
                            }}
                        >
                            {subscription.status}
                        </Tag>
                    </div>
                }
                cancelText="Close"
                closeButtonPosition="bottom"
                padding="0 1rem"
                customLeftButton={
                    hasHistoryData ? (
                        <OutlineButton onClick={handleOpenTimelineModal}>
                            Subscription History
                        </OutlineButton>
                    ) : null
                }
            >
                <div style={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '1rem'
                }}>
                    {/* Card Informative Section */}
                    <CardInformativeSection
                        subscription={subscription}
                        percentage={percentage}
                        daysLeft={daysLeft}
                        mainDomain={mainDomain}
                        color={color}
                    />

                    {loading && (
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '12.5rem'
                        }}>
                            <Spin size="large" />
                        </div>
                    )}

                    {/* Area per i grafici - Replicando esattamente DeviceAnalytics */}
                    {!loading && (
                        <ChartsConsumption
                            chartData={chartData}
                            barChartFill={barChartFill}
                            pieChartFill={pieChartFill}
                            tooltipStyle={tooltipStyle}
                            defs={defs}
                        />
                    )}
                </div>
            </FullScreenModal>

            {/* Timeline Reviews Modal */}
            <TimelineRenewalsModal
                visible={timelineModalVisible}
                onClose={handleCloseTimelineModal}
                subscription={subscription}
            />
        </>
    );
};

export default ConsumptionDetailsModal; 